import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, RefreshCw, Filter, Navigation, Clock, Wrench, Phone, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fix leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;

// Custom marker icons
const createIcon = (color) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
    <path d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.268 21.732 0 14 0z" fill="${color}" stroke="#fff" stroke-width="2"/>
    <circle cx="14" cy="14" r="6" fill="#fff"/>
  </svg>`;
  return new L.Icon({
    iconUrl: `data:image/svg+xml;base64,${btoa(svg)}`,
    iconSize: [28, 40],
    iconAnchor: [14, 40],
    popupAnchor: [0, -40],
  });
};

const greenIcon = createIcon('#22c55e');
const yellowIcon = createIcon('#eab308');
const redIcon = createIcon('#ef4444');
const grayIcon = createIcon('#9ca3af');

const statusConfig = {
  gps_active: { icon: greenIcon, label: 'GPS Aktiv', color: 'text-green-600', bg: 'bg-green-100', dot: 'bg-green-500' },
  gps_off: { icon: yellowIcon, label: 'GPS Bağlı', color: 'text-yellow-600', bg: 'bg-yellow-100', dot: 'bg-yellow-500' },
  offline: { icon: redIcon, label: 'Offline', color: 'text-red-600', bg: 'bg-red-100', dot: 'bg-red-500' },
  no_data: { icon: grayIcon, label: 'Məlumat yoxdur', color: 'text-gray-500', bg: 'bg-gray-100', dot: 'bg-gray-400' },
};

// Map auto-fit component
function FitBounds({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length > 0) {
      const bounds = L.latLngBounds(positions);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    }
  }, [positions, map]);
  return null;
}

export default function MapPanel({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [historyDialog, setHistoryDialog] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyDate, setHistoryDate] = useState(new Date().toISOString().slice(0, 10));
  const intervalRef = useRef(null);
  const token = localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    try {
      let url = `${API}/map/live`;
      if (selectedGroup && selectedGroup !== 'all') {
        url += `?group_id=${selectedGroup}`;
      }
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (error) {
      console.error('Map data error:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedGroup, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchData, 30000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, fetchData]);

  const fetchHistory = async (userId) => {
    try {
      const res = await axios.get(`${API}/map/history/${userId}?date=${historyDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistoryData(res.data);
    } catch (error) {
      toast.error('Tarixçə yüklənmədi');
    }
  };

  const filteredTechnicians = (data?.technicians || []).filter(t => {
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    return true;
  });

  const markersWithLocation = filteredTechnicians.filter(t => t.latitude && t.longitude);
  const positions = markersWithLocation.map(t => [t.latitude, t.longitude]);

  // Stats
  const stats = {
    total: filteredTechnicians.length,
    gps_active: filteredTechnicians.filter(t => t.status === 'gps_active').length,
    gps_off: filteredTechnicians.filter(t => t.status === 'gps_off').length,
    offline: filteredTechnicians.filter(t => t.status === 'offline').length,
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-zinc-600">Xəritə yüklənir...</span>
      </div>
    );
  }

  const defaultCenter = positions.length > 0
    ? [positions.reduce((s, p) => s + p[0], 0) / positions.length, positions.reduce((s, p) => s + p[1], 0) / positions.length]
    : [40.4093, 49.8671]; // Baku

  return (
    <div className="space-y-4" data-testid="map-panel">
      {/* Filters + Stats */}
      <div className="bg-white rounded-lg border border-zinc-200 p-4" style={{ position: 'relative', zIndex: 1000 }}>
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-zinc-500" />
            <span className="text-sm font-medium text-zinc-700">Xəritə</span>
          </div>
          {(data?.groups || []).length > 0 && (
            <Select value={selectedGroup} onValueChange={setSelectedGroup}>
              <SelectTrigger className="w-[180px]" data-testid="map-group-filter">
                <SelectValue placeholder="Bütün qruplar" />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={5} className="z-[9999] max-h-[300px] overflow-y-auto">
                <SelectItem value="all">Bütün qruplar</SelectItem>
                {(data?.groups || []).map(g => (
                  <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]" data-testid="map-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent position="popper" sideOffset={5} className="z-[9999]">
              <SelectItem value="all">Hamısı</SelectItem>
              <SelectItem value="gps_active">GPS Aktiv</SelectItem>
              <SelectItem value="gps_off">GPS Bağlı</SelectItem>
              <SelectItem value="offline">Offline</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline" size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? 'border-green-500 text-green-600' : ''}
            data-testid="auto-refresh-toggle"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${autoRefresh ? 'animate-spin' : ''}`} style={autoRefresh ? { animationDuration: '3s' } : {}} />
            {autoRefresh ? 'Avtomatik' : 'Manual'}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="map-refresh">
            Yenilə
          </Button>
        </div>

        {/* Status Stats */}
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-1.5 text-sm">
            <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>
            <span className="text-zinc-600">GPS Aktiv: <strong>{stats.gps_active}</strong></span>
          </div>
          <div className="flex items-center gap-1.5 text-sm">
            <span className="w-2.5 h-2.5 rounded-full bg-yellow-500"></span>
            <span className="text-zinc-600">GPS Bağlı: <strong>{stats.gps_off}</strong></span>
          </div>
          <div className="flex items-center gap-1.5 text-sm">
            <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
            <span className="text-zinc-600">Offline: <strong>{stats.offline}</strong></span>
          </div>
          <div className="flex items-center gap-1.5 text-sm text-zinc-500">
            Cəm: <strong>{stats.total}</strong>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden" style={{ height: '550px', position: 'relative', zIndex: 1 }}>
        <MapContainer
          center={defaultCenter}
          zoom={10}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {positions.length > 0 && <FitBounds positions={positions} />}

          {markersWithLocation.map(tech => {
            const config = statusConfig[tech.status] || statusConfig.no_data;
            return (
              <Marker
                key={tech.user_id}
                position={[tech.latitude, tech.longitude]}
                icon={config.icon}
              >
                <Popup>
                  <div className="min-w-[200px]">
                    <div className="font-bold text-sm mb-1">{tech.full_name}</div>
                    <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.color} mb-2`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`}></span>
                      {config.label}
                    </div>
                    {tech.phone && <div className="text-xs text-gray-600 mb-1">{tech.phone}</div>}
                    <div className="text-xs text-gray-500 mb-1">Qrup: {tech.group_names?.join(', ')}</div>
                    {tech.last_location_at && (
                      <div className="text-xs text-gray-500 mb-1">
                        Son GPS: {new Date(tech.last_location_at).toLocaleString('az-AZ')}
                      </div>
                    )}
                    <div className="flex gap-2 text-xs mt-1">
                      {tech.active_tickets > 0 && <span className="text-indigo-600">Servis: {tech.active_tickets}</span>}
                      {tech.active_connections > 0 && <span className="text-emerald-600">Qoşulma: {tech.active_connections}</span>}
                    </div>
                    <button
                      onClick={() => { setHistoryDialog(tech); setHistoryData(null); }}
                      className="mt-2 text-xs text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <Navigation className="h-3 w-3" /> Hərəkət tarixçəsi
                    </button>
                  </div>
                </Popup>
              </Marker>
            );
          })}

          {/* History polyline */}
          {historyData?.points?.length > 1 && (
            <Polyline
              positions={historyData.points.map(p => [p.latitude, p.longitude])}
              color="#3b82f6"
              weight={3}
              opacity={0.8}
            />
          )}
        </MapContainer>
      </div>

      {/* Technician List */}
      <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden">
        <div className="bg-zinc-800 px-4 py-3">
          <h3 className="text-sm font-bold text-white uppercase tracking-wide">Texnik Siyahısı ({filteredTechnicians.length})</h3>
        </div>
        <div className="divide-y divide-zinc-100 max-h-[300px] overflow-y-auto">
          {filteredTechnicians.map(tech => {
            const config = statusConfig[tech.status] || statusConfig.no_data;
            return (
              <div key={tech.user_id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-50">
                <span className={`w-3 h-3 rounded-full flex-shrink-0 ${config.dot}`}></span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-900 truncate">{tech.full_name}</div>
                  <div className="text-xs text-zinc-500">{tech.group_names?.join(', ')}</div>
                </div>
                <div className={`text-xs font-medium ${config.color}`}>{config.label}</div>
                {tech.last_location_at && (
                  <div className="text-xs text-zinc-400 hidden sm:block">
                    {new Date(tech.last_location_at).toLocaleTimeString('az-AZ', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                )}
                <div className="flex gap-1">
                  {tech.active_tickets > 0 && <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">{tech.active_tickets}</span>}
                  {tech.active_connections > 0 && <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">{tech.active_connections}</span>}
                </div>
                <Button
                  variant="ghost" size="sm"
                  onClick={() => { setHistoryDialog(tech); setHistoryData(null); }}
                  title="Tarixçə"
                >
                  <Eye className="h-4 w-4 text-zinc-400" />
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* History Dialog */}
      <Dialog open={!!historyDialog} onOpenChange={() => setHistoryDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Navigation className="h-5 w-5 text-blue-600" />
              Hərəkət Tarixçəsi - {historyDialog?.full_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input
                type="date"
                value={historyDate}
                onChange={(e) => setHistoryDate(e.target.value)}
                data-testid="history-date"
              />
              <Button onClick={() => historyDialog && fetchHistory(historyDialog.user_id)} data-testid="history-fetch">
                Göstər
              </Button>
            </div>
            {historyData && (
              <div className="text-sm">
                {historyData.points?.length > 0 ? (
                  <div>
                    <p className="text-zinc-600 mb-2">{historyData.points.length} nöqtə tapıldı</p>
                    <p className="text-xs text-zinc-500">
                      İlk: {new Date(historyData.points[0].timestamp).toLocaleTimeString('az-AZ')} - 
                      Son: {new Date(historyData.points[historyData.points.length - 1].timestamp).toLocaleTimeString('az-AZ')}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">Marşrut xəritədə mavi xətt ilə göstərilir</p>
                  </div>
                ) : (
                  <p className="text-zinc-500">Bu tarixdə məlumat yoxdur</p>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
